/**
 * `<EndCombatPanel>` — the GM's END COMBAT gate (`between-encounter-flow` 1.3, Decision 1 = A).
 * The live fight's "● Live — End combat" button opens this, so a fight's wear reaches the pilot
 * sheets without anyone remembering a CLI verb. Ending marks the fight over (`end_combat`: turn
 * commands and intents are refused, the round header reads COMBAT OVER), and the server then
 * switches the table to Narrative, so every screen leaves the board (`GameRoom.followCombatOver`,
 * `decisions/0203` point 2, amended 2026-10-05).
 *
 * It renders `planCommit`'s rows (the same plan `lvtt encounter commit` applies, so the browser
 * re-derives none of "what carries") and offers three choices:
 *   - **Commit and end** dispatches the plan's ONE roster `batch` (filed under
 *     `encounterCommitScope`, so one undo reverts the whole commit), then ends the fight
 *     (`onEnd`, which dispatches `end_combat`). A rejection of either keeps the panel open and
 *     names the error; a rejected commit leaves the fight live.
 *   - **End without committing** ends the fight and leaves the roster alone (a scratch fight).
 *   - **Cancel** closes the panel; nothing changes.
 * **After the fight is over** (no `onEnd`) it is the COMMIT WEAR gate instead: the same rows, and
 * **Commit wear** dispatches the same batch and closes the panel, so ending without a commit (the
 * Director, `lvtt encounter end`, End without committing) never strands the fight's wear. With the
 * table back on Narrative, the GM reaches it by previewing the fight (*Peek* on ENCOUNTERS).
 * A keyed allied NPC (`persistentNpcKey`, `decisions/0196`) gets a row too ("ROCKET (rocket):
 * HP 27→18"); the same batch writes its `npcWear` entry.
 * A re-commit (`encounterCommits[encounterId]` set) warns, and still allows: commit writes
 * absolute values, so it overwrites any Repairs spent since.
 *
 * GM-only by its call sites (`CombatApp`'s END COMBAT and COMMIT WEAR), and the roster gate makes the batch GM-only on the
 * server too (`record_encounter_commit`, `permissions.ts`).
 */
import { useState } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { EncounterState, RosterCommand, RosterState } from "@lancer-vtt/domain";
import { planCommit, type CommitRow, type NpcCommitRow } from "@lancer-vtt/rules";
import type { DispatchResult } from "../../../lib/roster/remoteRosterController";
import { printedItemName } from "../../../lib/roster/printedMechLabels";
import "./end-combat-panel.css";

/** A row's changes as short phrases ("Structure 4→2"); empty when the fight left the mech as it was. */
export function commitRowChanges(row: CommitRow, name: (key: string) => string): string[] {
  const { before, after } = row;
  const out: string[] = [];
  if (after.destroyed && after.destroyed !== before.destroyed) out.push(after.destroyed === "vaporized" ? "VAPORIZED" : "DESTROYED (wreck)");
  const tracks = [
    ["HP", "hp"],
    ["Structure", "structure"],
    ["Stress", "stress"],
    ["Repairs", "repairsRemaining"],
  ] as const;
  for (const [label, key] of tracks) {
    // A destroyed mech's tracks are moot (the commit writes only the marker); Repairs still carry.
    if (after.destroyed && key !== "repairsRemaining") continue;
    if (before[key] !== after[key]) out.push(`${label} ${before[key]}→${after[key]}`);
  }
  for (const key of after.destroyedItems) {
    if (!before.destroyedItems.includes(key)) out.push(`destroyed ${name(key)}`);
  }
  return out;
}

/** A keyed NPC row's changes as short phrases ("HP 27→18"); empty when the fight left it as it was. */
export function npcCommitRowChanges(row: NpcCommitRow): string[] {
  const { before, after } = row;
  if (after.destroyed) return before.destroyed ? [] : ["DESTROYED"];
  const out: string[] = before.destroyed ? ["no longer destroyed"] : [];
  const tracks = [["HP", "hp"], ["Structure", "structure"], ["Stress", "stress"]] as const;
  for (const [label, key] of tracks) if (before[key] !== after[key]) out.push(`${label} ${before[key]}→${after[key]}`);
  return out;
}

export function EndCombatPanel({
  state,
  roster,
  compendium,
  encounterId,
  actorId,
  dispatch,
  onEnd,
  onClose,
}: {
  state: EncounterState;
  /** The roster the commit reads and writes; `null` while it loads (commit waits for it). */
  roster: RosterState | null;
  compendium: Compendium;
  encounterId: string;
  actorId: string;
  dispatch: (command: RosterCommand) => DispatchResult;
  /** End the fight (`end_combat`) and close the panel; the result names a rejection. The server
   *  then puts the table on Narrative. Omitted once the fight is over: the panel then
   *  only commits. */
  onEnd?: () => DispatchResult;
  /** Close the panel: Cancel, or a landed commit when the fight is already over. */
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  // The commit's timestamp is when the GM opened the panel, stable across re-renders.
  const [now] = useState(() => new Date().toISOString());
  const plan = roster ? planCommit({ state, roster, compendium, encounterId, now, actorId }) : null;

  const commit = () => {
    if (!plan) return;
    const res = dispatch(plan.batch);
    if (!res.ok) {
      setError(`Commit rejected: ${res.error}`);
      return;
    }
    if (onEnd) end(onEnd);
    else onClose();
  };

  const end = (endFight: () => DispatchResult) => {
    const res = endFight();
    if (!res.ok) setError(`End rejected: ${res.error}`);
  };

  return (
    <div className="ecp-scrim">
      <div className="ecp" role="dialog" aria-label={onEnd ? "End combat" : "Commit wear"}>
        <div className="ecp-title">{onEnd ? "END COMBAT — " : "COMBAT OVER — "}COMMIT WEAR TO THE ROSTER?</div>
        {!plan ? (
          <div className="ecp-note">Roster loading…</div>
        ) : (
          <>
            {plan.priorCommitAt && (
              <div className="ecp-warn" role="alert">
                Already committed at {plan.priorCommitAt}; committing again overwrites any Repairs spent since.
              </div>
            )}
            {plan.rows.length === 0 && plan.npcRows.length === 0 && plan.pilotRows.length === 0 ? (
              <div className="ecp-note">No roster mechs in this fight — nothing to commit.</div>
            ) : (
              <ul className="ecp-rows">
                {plan.rows.map((row) => {
                  const pilot = roster!.pilots[row.pilotId]!;
                  const changes = commitRowChanges(row, (key) => printedItemName({ pilot, mechId: row.mechId, key, compendium }));
                  return (
                    <li key={row.combatantId} className="ecp-row" data-combatant={row.combatantId}>
                      <span className="ecp-name">{row.combatantName}</span>
                      <span className="ecp-changes">{changes.length > 0 ? changes.join(", ") : "unchanged"}</span>
                    </li>
                  );
                })}
                {plan.npcRows.map((row) => {
                  const changes = npcCommitRowChanges(row);
                  return (
                    <li key={row.combatantId} className="ecp-row" data-combatant={row.combatantId}>
                      <span className="ecp-name">
                        {row.combatantName} ({row.key})
                      </span>
                      <span className="ecp-changes">{changes.length > 0 ? changes.join(", ") : "unchanged"}</span>
                    </li>
                  );
                })}
                {plan.pilotRows.map((row) => (
                  <li key={row.combatantId} className="ecp-row" data-combatant={row.combatantId}>
                    <span className="ecp-name">{row.combatantName}</span>
                    <span className="ecp-changes">{row.before !== row.after ? `HP ${row.before}→${row.after}` : "unchanged"}</span>
                  </li>
                ))}
              </ul>
            )}
            {plan.skipped.length > 0 && (
              <ul className="ecp-skips">
                {plan.skipped.map((s) => (
                  <li key={s.combatantId}>
                    skipped {state.combatants[s.combatantId]?.name ?? s.combatantId}: {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {error && (
          <div className="ecp-error" role="alert">
            {error}
          </div>
        )}
        <div className="ecp-actions">
          <button type="button" className="ecp-btn ecp-btn-primary" disabled={!plan} onClick={commit}>
            {onEnd ? "Commit and end" : "Commit wear"}
          </button>
          {onEnd && (
            <button type="button" className="ecp-btn" onClick={() => end(onEnd)}>
              End without committing
            </button>
          )}
          <button type="button" className="ecp-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
